import { Box, Container, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  colorLandingBrand,
  colorLandingBrandLine,
  colorLandingBrandSoft,
  colorLandingBrandStrong,
  colorLandingLine,
  colorLandingMuted,
  fontLandingDisplay,
} from "src/design/tokens";
import { SectionHeading } from "./components/SectionHeading";
import { DashboardMock } from "./components/DashboardMock";
import { BookingPageMock } from "./components/BookingPageMock";
import appBookingScreen from "src/assets/landing/app-booking-screen.png";

/**
 * Two-way arrow used between the customer-facing entry points and the
 * dashboard: horizontal from md up, branching downwards on small screens.
 */
const FlowArrows = () => (
  <>
    <Box
      aria-hidden="true"
      component="svg"
      viewBox="0 0 70 44"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      sx={{
        display: { xs: "none", md: "block" },
        width: 64,
        flexShrink: 0,
        color: colorLandingBrand,
      }}
    >
      <path d="M6 15 H60" />
      <path d="M52 8 l9 7 -9 7" />
      <path d="M64 29 H10" />
      <path d="M18 22 l-9 7 9 7" />
    </Box>
    <Box
      aria-hidden="true"
      component="svg"
      viewBox="0 0 260 92"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
      sx={{
        display: { xs: "block", md: "none" },
        width: 284,
        maxWidth: "92%",
        mx: "auto",
        color: colorLandingBrand,
      }}
    >
      <path d="M60 48 H200" />
      <path d="M60 48 V16" />
      <path d="M52 24 l8 -8 8 8" />
      <path d="M200 48 V16" />
      <path d="M192 24 l8 -8 8 8" />
      <path d="M130 48 V80" />
      <path d="M122 72 l8 8 8 -8" />
    </Box>
  </>
);

interface SourceTagProps {
  label: string;
  highlighted?: boolean;
}

const SourceTag = ({ label, highlighted }: SourceTagProps) => (
  <Typography
    sx={{
      display: "block",
      mt: 1.125,
      textAlign: "center",
      fontSize: "0.8rem",
      fontWeight: 600,
      ...(highlighted
        ? {
            color: colorLandingBrandStrong,
            backgroundColor: colorLandingBrandSoft,
            border: `1px solid ${colorLandingBrandLine}`,
            borderRadius: "9px",
            px: 1.25,
            py: 0.875,
          }
        : { color: colorLandingMuted }),
    }}
  >
    {label}
  </Typography>
);

/**
 * Shows the platform as a loop: customers book from the consumer app or the
 * restaurant's hosted booking page, while offers and events published in the
 * dashboard flow back out to the app.
 */
export const EcosystemSection = () => {
  const { t } = useTranslation();

  return (
    <Box
      component="section"
      id="ecosysteme"
      sx={{ py: { xs: 8, md: 11.5 }, borderTop: `1px solid ${colorLandingLine}` }}
    >
      <Container maxWidth="lg">
        <SectionHeading
          title={t("landingPage.ecosystem.title")}
          subtitle={t("landingPage.ecosystem.subtitle")}
        />

        <Box
          sx={{
            mt: 5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 0.5, md: 1.25 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
              flexShrink: 0,
              transform: { xs: "scale(0.62)", sm: "scale(0.8)", md: "none" },
              transformOrigin: "center",
            }}
          >
            <Box sx={{ flexShrink: 0 }}>
              <Box
                component="img"
                src={appBookingScreen}
                alt={t("landingPage.ecosystem.appScreenAlt")}
                sx={{ width: 200, height: "auto", display: "block", mx: "auto" }}
              />
              <SourceTag label={t("landingPage.ecosystem.appTag")} />
            </Box>

            <Box
              aria-hidden="true"
              sx={{
                fontFamily: fontLandingDisplay,
                fontSize: 32,
                lineHeight: 1,
                color: colorLandingBrand,
                flexShrink: 0,
              }}
            >
              +
            </Box>

            <Box sx={{ flexShrink: 0 }}>
              <BookingPageMock sx={{ mx: "auto" }} />
              <SourceTag label={t("landingPage.ecosystem.bookingPageTag")} highlighted />
            </Box>
          </Box>

          <FlowArrows />

          <DashboardMock
            sx={{
              flexShrink: 0,
              width: { xs: "100%", md: 400 },
              maxWidth: { xs: 460, md: 400 },
            }}
          />
        </Box>

        <Typography
          sx={{
            mt: 3.25,
            mx: "auto",
            maxWidth: "62ch",
            textAlign: "center",
            fontSize: 15,
            lineHeight: 1.6,
            color: colorLandingMuted,
          }}
        >
          {t("landingPage.ecosystem.caption")}
        </Typography>
      </Container>
    </Box>
  );
};
